// useProjectObjectives — the learning objectives of the project the Studio
// shows, with their coverage (the Learning objectives page and the outline
// row's dot). Shell-side, like useProjectIssues.ts: it asks the server
// whenever the project or the library changes (an objective added or taken
// off, a part written or linked moves the coverage), and hands the answer
// to the Studio. A failed ask (offline) keeps the last answer; another
// project's answer is never shown.

export { useProjectObjectives };

import { Result } from "@bilda/utils";
import { useEffect, useState } from "react";
import type { ProjectObjectives } from "../../ports/project.js";
import type { AppPorts, Library } from "../../ports.js";

type Answer = {
    readonly projectId: string;
    readonly objectives: ProjectObjectives;
};

const useProjectObjectives = (
    ports: AppPorts,
    projectId: string | null,
    library: Library | null,
): ProjectObjectives | undefined => {
    const [answer, setAnswer] = useState<Answer | undefined>(undefined);
    useEffect(() => {
        if (library === null || projectId === null) return;
        let current = true;
        const go = async () => {
            const r = await ports.loadProjectObjectives(projectId);
            if (current && Result.isOk(r))
                setAnswer({ projectId, objectives: r.value });
        };
        void go();
        return () => {
            current = false;
        };
    }, [ports, projectId, library]);
    return answer?.projectId === projectId ? answer.objectives : undefined;
};
